"use client";

import type { ReactNode } from "react";
import { Button } from "@/components/ui/Button";
import { DiscountCodeForm, TotalPriceOff } from "@/features/cart/DiscountCodeForm";
import { useCart } from "@/features/cart/hooks";
import { useTranslations } from "@/i18n/client";

export interface CartSummaryProps {
	variant?: "checkout" | "default";
}

// Replaces the numeric amount in an already-localized price string (e.g.
// Centra formats NOK as "4 999 NOK", grouping thousands with a
// non-breaking space) with a different number, preserving everything else —
// currency prefix/suffix, decimal separator, and thousands grouping style.
const withAmount = (formatted: string | undefined, amount: number): string | undefined => {
	if (!formatted) return formatted;

	// Consume digits, plus any whitespace that's itself followed by another
	// digit — so a thousands-separating space is included, but the space
	// before a currency suffix (e.g. the " " before "NOK") is not.
	const match = formatted.match(/\d(?:\d|\s(?=\d))*(?:([.,])(\d+))?/);
	if (!match) return formatted;

	const [wholeMatch, decimalSeparator, fraction] = match;
	const decimals = fraction?.length ?? 0;
	const [whole, decimalDigits] = amount.toFixed(decimals).split(".");
	const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
	const newAmount = decimalDigits ? `${grouped}${decimalSeparator}${decimalDigits}` : grouped;

	return formatted.replace(wholeMatch, newAmount);
};

export const CartSummary = ({ variant = "default" }: CartSummaryProps) => {
	const { data } = useCart();

	const t = useTranslations("cart");

	if (!data?.hasItems) {
		return null;
	}

	const isFreeShipping = !data.shippingPriceAsNumber || data.shippingPriceAsNumber === 0;
	const shippingValue = isFreeShipping ? t("free") : data.shippingPrice;

	const totalPriceOff = (data?.vouchers ?? []).reduce((acc, voucher) => {
		return acc + (voucher.priceOffAsNumber ?? 0) + (voucher.shippingDiscountAsNumber ?? 0);
	}, 0);
	const hasDiscount = totalPriceOff < 0;

	// The donation is a real selection line (and so is already folded into
	// `subtotal`), but it's shown as its own row rather than in the product
	// list — pull it back out of the subtotal so the breakdown still adds up.
	const donation = data.donation;
	const donationAsNumber = donation?.totalPriceAsNumber ?? donation?.priceEachAsNumber ?? 0;
	const subtotalValue = donation
		? withAmount(data.subtotal, (data.subtotalAsNumber ?? 0) - donationAsNumber)
		: data.subtotal;

	const breakdownRows = (
		<>
			<Row label={t("subtotal")} value={subtotalValue} />
			{donation && (
				<Row label={donation.product?.name ?? t("donation")} value={donation.totalPrice} />
			)}
			{variant === "checkout" && hasDiscount && (
				<Row label={t("discountCode")} value={<TotalPriceOff discount={totalPriceOff} />} />
			)}
			<Row
				label={t("shipping")}
				value={shippingValue}
				valueClassName={isFreeShipping ? "text-status-success-text" : undefined}
			/>
			<div className="flex flex-col gap-grid-component-spacing-5xs">
				<div className="flex items-end justify-between font-body font-regular text-base leading-[1.5] text-text-dark-text-1">
					<span>{t("total")}</span>
					<span className="text-right">{data.total}</span>
				</div>
				{data.vat && (
					<div className="flex items-end justify-between font-body font-regular text-2xs leading-[1.5] text-text-dark-text-2">
						<span>{t("vat")}</span>
						<span className="text-right">{data.vat}</span>
					</div>
				)}
			</div>
		</>
	);

	if (variant === "checkout") {
		return (
			<>
				<div className="flex flex-col gap-grid-component-spacing-md py-grid-component-spacing-md border-y border-border-medium">
					{breakdownRows}
				</div>
				{!hasDiscount && (
					<div className="py-grid-component-spacing-md border-b border-border-medium">
						<DiscountCodeForm />
					</div>
				)}
			</>
		);
	}

	return (
		<div className="flex flex-col gap-grid-component-spacing-md py-grid-component-spacing-md border-y border-border-medium">
			{breakdownRows}
			<Button as="a" href="/checkout" size="large" color="primary">
				{t("checkout")}
			</Button>
		</div>
	);
};

const Row = ({
	label,
	value,
	valueClassName,
}: {
	label: string;
	value: string | ReactNode | undefined;
	valueClassName?: string;
}) => (
	<div className="flex items-end justify-between font-body font-regular text-xs leading-[1.15] text-text-dark-text-1 uppercase tabular-nums lining-nums">
		<span>{label}</span>
		<span className={`text-right ${valueClassName ?? ""}`}>{value}</span>
	</div>
);
